import {
  DEFAULT_TRANSACTION_TITLES,
  PRIMARY_CASH_WALLET,
  TRANSACTION_TITLE_PARAM_KEY,
} from "../../constants";
import type { ConditionSpecIn } from "../../types";
import type { WizardStepFormValue } from "./toWizardCampaignIn";

/**
 * Named bet-targeted campaign presets (BONENG-896). Each composes existing
 * primitives — step 1 `campaign_start` OFFER + step 2 trigger/conditions/result
 * — so operators don't hand-assemble Bet and Get / No Sweat Bet / Parlay
 * Insurance shapes. Reachable only when the wizard is (already gated by
 * `promotions.campaigns` / PAM `dynamicBonuses`).
 */
export type WizardBetTargetedPreset =
  | "bet_and_get"
  | "no_sweat_bet"
  | "parlay_insurance";

export type BetTargetedPresetDefinition = {
  id: WizardBetTargetedPreset;
  step2EventKey: string;
  step2ResultTypeKey: string;
  step2ResultParams: Record<string, unknown>;
  injectedStep2Conditions: ConditionSpecIn[];
};

/**
 * Injected on qualifying follow-up steps so step 2 only fires for the bet the
 * step-1 promo was applied to. Hidden from PAM condition authoring — never
 * selectable in the conditions dropdown; operators control it through the
 * step-2 "Step 1 Promo Applied" toggle instead (BONENG-962, on by default).
 */
export const APPLIED_OFFER_MATCHES_TYPE = "applied_offer_matches";

/**
 * Step-1 result type keys whose follow-up steps may target the promo the user
 * applied to a bet (BONENG-962): the show_bonus OFFER banner, a Profit Boost,
 * or Free Picks.
 */
export const APPLIED_OFFER_STEP_ONE_RESULT_KEYS: ReadonlySet<string> = new Set([
  "show_bonus",
  "create_profit_boost_promotion",
  "award_free_picks",
]);

/**
 * Sportsbook bet events that carry `applied_offer_bonus_id` (mirrors the
 * backend's sportsbook event seeds, BONENG-896). `applied_offer_matches` can
 * only evaluate against these triggers, so the step-2 toggle is confined to
 * them — never deposits, casino, or other non-bet events (BONENG-962).
 * Not derived from the catalog — update by hand when the backend
 * sportsbook-event seeds change (see BONENG-896).
 */
export const APPLIED_OFFER_SPORTSBOOK_EVENT_KEYS: ReadonlySet<string> = new Set([
  "bet_placed",
  "placed_live_bet",
  "places_bet_rebet_cash",
  "places_profit_boost_bet",
  "parlay_loss_by_one",
  "settled_cash_bet",
  "settled_sweat_bet",
  "settled_profit_boost_bet",
]);

export const APPLIED_OFFER_MATCHES_CONDITION: ConditionSpecIn = {
  type: APPLIED_OFFER_MATCHES_TYPE,
};

export const BET_TARGETED_PRESET_DEFINITIONS: Record<
  WizardBetTargetedPreset,
  BetTargetedPresetDefinition
> = {
  bet_and_get: {
    id: "bet_and_get",
    step2EventKey: "bet_placed",
    step2ResultTypeKey: "award_free_picks",
    step2ResultParams: { split_count: "4" },
    // applied_offer_matches is injected for every opt-in follow-up (see
    // injectedFollowUpConditionsFor) — keep presets free of that type.
    injectedStep2Conditions: [
      { type: "min_event_amount", params: { amount: "10.00" } },
      {
        type: "event_field_equals",
        params: { field: "money_type", value: "rebet_cash" },
      },
    ],
  },
  no_sweat_bet: {
    id: "no_sweat_bet",
    // Dedicated No Sweat settlement event (BONENG-383); plan also allows
    // settled_cash_bet — sweat is preferred because the producer already
    // scopes to rebet_cash stakes.
    step2EventKey: "settled_sweat_bet",
    step2ResultTypeKey: "refund_on_loss",
    step2ResultParams: {
      max_refund_amount: "50.00",
      wallet: "rebet_free_cash",
    },
    injectedStep2Conditions: [
      {
        type: "event_field_equals",
        params: { field: "bet_result", value: "lost" },
      },
    ],
  },
  parlay_insurance: {
    id: "parlay_insurance",
    step2EventKey: "parlay_loss_by_one",
    step2ResultTypeKey: "refund_on_loss",
    step2ResultParams: {
      max_refund_amount: "25.00",
      wallet: "rebet_free_cash",
    },
    injectedStep2Conditions: [
      {
        type: "event_field_compare",
        params: { field: "parlay_legs", op: "gte", value: "2" },
      },
    ],
  },
};

export const BET_TARGETED_PRESET_IDS: WizardBetTargetedPreset[] = [
  "bet_and_get",
  "no_sweat_bet",
  "parlay_insurance",
];

/**
 * Match a named preset from step-2 event + result. Hydrate must not call
 * this — an inferred preset plus the seed effect can rewrite authored steps.
 */
export const inferWizardBetTargetedPreset = (
  steps:
    | Array<{ event_key?: string; result_type_key?: string } | undefined>
    | undefined,
): WizardBetTargetedPreset | null => {
  const step2 = steps?.[1];
  if (!step2?.event_key || !step2.result_type_key) return null;
  return (
    BET_TARGETED_PRESET_IDS.find((id) => {
      const definition = BET_TARGETED_PRESET_DEFINITIONS[id];
      return (
        definition.step2EventKey === step2.event_key &&
        definition.step2ResultTypeKey === step2.result_type_key
      );
    }) ?? null
  );
};

export const isWizardBetTargetedPreset = (
  value: unknown,
): value is WizardBetTargetedPreset =>
  typeof value === "string" && value in BET_TARGETED_PRESET_DEFINITIONS;

/**
 * True when a follow-up step can offer the "Step 1 Promo Applied" toggle
 * (BONENG-962): step 1's result is one of APPLIED_OFFER_STEP_ONE_RESULT_KEYS
 * and the follow-up step triggers on a sportsbook bet event that carries
 * `applied_offer_bonus_id`. Callers only ask for follow-up steps — the toggle
 * (and the condition) never appears on step 1.
 */
export const appliedOfferToggleAvailable = (
  stepOneResultTypeKey: string | undefined,
  followUpEventKey: string | undefined,
): boolean =>
  APPLIED_OFFER_STEP_ONE_RESULT_KEYS.has(stepOneResultTypeKey ?? "") &&
  APPLIED_OFFER_SPORTSBOOK_EVENT_KEYS.has(followUpEventKey ?? "");

/**
 * Conditions PAM injects on follow-up steps. `applied_offer_matches` is
 * included when the step qualifies for the toggle (see
 * `appliedOfferToggleAvailable`) and the operator hasn't switched it off —
 * `undefined` means "default on" (BONENG-962). Preset seed conditions live in
 * the editable form `conditions` array (BONENG-984) and are not re-injected.
 */
export const injectedFollowUpConditionsFor = (
  stepOneResultTypeKey: string | undefined,
  followUpEventKey: string | undefined,
  appliedOfferEnabled?: boolean,
): ConditionSpecIn[] =>
  appliedOfferToggleAvailable(stepOneResultTypeKey, followUpEventKey) &&
  appliedOfferEnabled !== false
    ? [APPLIED_OFFER_MATCHES_CONDITION]
    : [];

/**
 * Types stripped from form conditions before re-injecting on follow-up steps.
 * `applied_offer_matches` is stripped only where the step-2 toggle owns it
 * (`appliedOfferOwned`, i.e. `appliedOfferToggleAvailable` for that step) —
 * on non-qualifying steps a stored copy is preserved verbatim so re-saving a
 * pre-BONENG-962 campaign never silently drops it.
 */
export const followUpInjectedConditionTypes = (
  appliedOfferOwned: boolean = true,
): ReadonlySet<string> =>
  new Set(appliedOfferOwned ? [APPLIED_OFFER_MATCHES_TYPE] : []);

/** `{}` for result types with no ledger row, so nothing is seeded for them. */
const transactionTitleDefaultFor = (
  resultTypeKey: string,
): Record<string, string> => {
  const title = DEFAULT_TRANSACTION_TITLES[resultTypeKey];
  return title === undefined ? {} : { [TRANSACTION_TITLE_PARAM_KEY]: title };
};

const cloneConditions = (conditions: ConditionSpecIn[]): ConditionSpecIn[] =>
  conditions.map((condition) => ({
    type: condition.type,
    ...(condition.params ? { params: { ...condition.params } } : {}),
  }));

/** Seed starting-point steps — fully editable after selection (BONENG-984). */
export const stepsForBetTargetedPreset = (
  preset: WizardBetTargetedPreset,
  options?: { seedPrimaryCashWallet?: boolean },
): WizardStepFormValue[] => {
  const definition = BET_TARGETED_PRESET_DEFINITIONS[preset];
  const step2ResultParams: Record<string, unknown> = {
    // The blueprint sets the result type without going through
    // applyResultDefaults, so seed the transaction title here as well —
    // otherwise preset campaigns are authored with a blank one (BONENG-960).
    ...transactionTitleDefaultFor(definition.step2ResultTypeKey),
    ...definition.step2ResultParams,
  };
  // BONENG-1236: Credit Wallet cash result persists rebet_cash (no Restriction
  // chip). Missing wallet stays the legacy Free Pick default on the backend.
  if (
    options?.seedPrimaryCashWallet &&
    definition.step2ResultTypeKey === "refund_on_loss"
  ) {
    step2ResultParams.wallet = PRIMARY_CASH_WALLET;
  }
  return [
    {
      result_type_key: "show_bonus",

      conditions: [],
    },
    {
      event_key: definition.step2EventKey,
      result_type_key: definition.step2ResultTypeKey,
      result_params: step2ResultParams,
      conditions: cloneConditions(definition.injectedStep2Conditions),
    },
  ];
};
